/** Original procedural workshop props. All geometry uses a 16-unit tile. */

import * as THREE from 'three'
import { TILE } from '../engine/constants'
import { spriteTexture } from './textures'

/** Half a tile: the radius at which a coin fills its cell. */
const R = TILE / 2

function lambert(
  colour: number,
  opts: Partial<THREE.MeshLambertMaterialParameters> = {},
): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: colour, ...opts })
}

/** A faceted spark shard with a copper equatorial band. */
function coinModel(): THREE.Object3D {
  const group = new THREE.Group()
  const core = new THREE.Mesh(
    new THREE.OctahedronGeometry(R * 0.92),
    new THREE.MeshStandardMaterial({
      color: 0x85ded1,
      emissive: 0x245d61,
      roughness: 0.3,
      metalness: 0.25,
    }),
  )
  core.scale.set(0.7, 1, 0.4)
  group.add(core)
  const band = new THREE.Mesh(
    new THREE.TorusGeometry(R * 0.48, TILE * 0.045, 4, 4),
    lambert(0xe8b971),
  )
  band.rotation.z = Math.PI / 4
  group.add(band)
  return group
}

/**
 * A solid block: a cube with the tile art on the front face and a darker, flat
 * material on the sides and back. The art was drawn flat, so lighting the front
 * face strongly and the rest barely is what sells the depth without inventing
 * side textures that do not exist.
 */
function blockModel(faceKey: string): THREE.Object3D {
  const group = new THREE.Group()

  const side = new THREE.Mesh(
    new THREE.BoxGeometry(TILE, TILE, TILE * 0.7),
    lambert(0x4a3018),
  )
  group.add(side)

  // A thin quad just proud of the cube's front face carries the pixel art.
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(TILE, TILE),
    new THREE.MeshBasicMaterial({
      map: spriteTexture(faceKey),
      transparent: true,
    }),
  )
  face.position.z = TILE * 0.351
  group.add(face)

  return group
}

/** A ground tile: a wide slab, so a run of them reads as solid mass. */
function groundModel(faceKey: string): THREE.Object3D {
  const group = new THREE.Group()
  const slab = new THREE.Mesh(
    new THREE.BoxGeometry(TILE, TILE, TILE * 0.8),
    lambert(0x3c5663),
  )
  group.add(slab)
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(TILE, TILE),
    new THREE.MeshBasicMaterial({
      map: spriteTexture(faceKey),
      transparent: true,
    }),
  )
  face.position.z = TILE * 0.401
  group.add(face)
  return group
}

/** A pipe: a capped cylinder, wider than a tile, standing on the ground. */
function pipeModel(): THREE.Object3D {
  const group = new THREE.Group()
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(TILE * 0.6, TILE * 0.6, TILE * 1.6, 18),
    lambert(0x9c694f),
  )
  shaft.position.y = -TILE * 0.2
  group.add(shaft)
  const lip = new THREE.Mesh(
    new THREE.CylinderGeometry(TILE * 0.78, TILE * 0.78, TILE * 0.4, 18),
    lambert(0xd59159),
  )
  lip.position.y = TILE * 0.5
  group.add(lip)
  return group
}

/**
 * A spring: a real helix swept as a tube, so it compresses by scaling Y and
 * the coils visibly bunch up. The pixel spring could only ever squash.
 */
function springModel(): THREE.Object3D {
  const points: THREE.Vector3[] = []
  const coils = 4
  const steps = coils * 12
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const angle = t * Math.PI * 2 * coils
    points.push(
      new THREE.Vector3(
        Math.cos(angle) * TILE * 0.34,
        (t - 0.5) * TILE * 0.9,
        Math.sin(angle) * TILE * 0.34,
      ),
    )
  }
  const curve = new THREE.CatmullRomCurve3(points)
  const mesh = new THREE.Mesh(
    new THREE.TubeGeometry(curve, steps, TILE * 0.1, 6, false),
    lambert(0x9090a8),
  )
  return mesh
}

/** A shield prism: an extruded eight-edge crystal, built from a THREE.Shape. */
function prismModel(): THREE.Object3D {
  const shape = new THREE.Shape()
  const points = 4
  const outer = R * 0.92
  const inner = R * 0.64
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outer : inner
    const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2
    const x = Math.cos(angle) * radius
    const y = Math.sin(angle) * radius
    if (i === 0) shape.moveTo(x, y)
    else shape.lineTo(x, y)
  }
  shape.closePath()
  const mesh = new THREE.Mesh(
    new THREE.ExtrudeGeometry(shape, {
      depth: TILE * 0.18,
      bevelEnabled: true,
      bevelThickness: TILE * 0.05,
      bevelSize: TILE * 0.05,
      bevelSegments: 1,
    }),
    new THREE.MeshStandardMaterial({
      color: 0xb77eb6,
      emissive: 0x463554,
      roughness: 0.4,
      metalness: 0.1,
    }),
  )
  mesh.geometry.translate(0, 0, -TILE * 0.09)
  return mesh
}

/** A checkpoint or goal pole: a thin shaft with a ball on top. */
function poleModel(): THREE.Object3D {
  const group = new THREE.Group()
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(TILE * 0.09, TILE * 0.09, TILE * 3, 8),
    lambert(0xa8a8b0),
  )
  shaft.position.y = -TILE * 0.5
  group.add(shaft)
  return group
}

/** A spinning saw blade: a low cylinder with teeth, turning about Z. */
function sawModel(): THREE.Object3D {
  const group = new THREE.Group()
  const blade = new THREE.Mesh(
    new THREE.CylinderGeometry(R * 0.8, R * 0.8, TILE * 0.12, 12),
    lambert(0x8c8c98),
  )
  blade.geometry.rotateX(Math.PI / 2)
  group.add(blade)
  const teeth = new THREE.Mesh(
    new THREE.TorusGeometry(R * 0.78, TILE * 0.09, 4, 12),
    lambert(0xc8c8d4),
  )
  group.add(teeth)
  return group
}

/**
 * Props with real geometry, keyed by the id the simulation uses. An id absent
 * from this table falls back to a textured quad, so adding a def to the object
 * registry never breaks rendering -- it just gets the 2D treatment until
 * someone models it.
 */
const MODELS: Record<string, () => THREE.Object3D> = {
  coin: coinModel,
  'coin-pop': coinModel,
  // The two remaining spin frames are mapped too, as a belt-and-braces guard.
  // `visuals.ts` now canonicalises the coin family to `coin`, so these should be
  // unreachable -- but if a future change lets a frame key through, a flat quad
  // flickering in among real coins is a far worse failure than a redundant map
  // entry, and this costs one line.
  'coin-frame2': coinModel,
  'coin-frame3': coinModel,
  prism: prismModel,
  pipe: pipeModel,
  spring: springModel,
  saw: sawModel,
  'saw-h': sawModel,
  checkpoint: poleModel,
  goal: poleModel,
}

/** Block faces that get a cube rather than a flat quad. */
const BLOCK_FACES: Record<string, string> = {
  'crate-block': 'crate-block',
  'crate-block-used': 'crate-block-used',
  'hard-block': 'hard-block',
  brick: 'brick',
  'onoff-block': 'onoff-block',
  'onoff-block-off': 'onoff-block-off',
}

/** Terrain tiles that get a slab rather than a flat quad. */
const GROUND_FACES: Record<string, string> = {
  ground: 'ground',
  'ground-top': 'ground-top',
  platform: 'platform',
}

/**
 * Build the 3D model for a sprite key, or null when the art should stay a
 * textured quad. `key` is the resolved visual key, so a prop that changes
 * appearance with state (a spent crate block) gets the right geometry.
 */
export function buildModel(key: string): THREE.Object3D | null {
  const prop = MODELS[key]
  if (prop !== undefined) return prop()

  const block = BLOCK_FACES[key]
  if (block !== undefined) return blockModel(block)

  const ground = GROUND_FACES[key]
  if (ground !== undefined) return groundModel(ground)

  return null
}
